import { ReactNode } from "react";
import { PaymentsSubscriptionStatusResponseDto } from "@advanced-sales-technologies/backend-client/models/PaymentsSubscriptionStatusResponseDto";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

import { Subscription } from "@/types/Subscription.type";
import useSubscription from "@/hooks/useSubscription";
import Badge from "@/components/atoms/Badge";
import SubscriptionItemButton from "@/components/molecules/subscription/SubscriptionItemButton";
import SubscriptionItemFeature from "@/components/molecules/subscription/SubscriptionItemFeature";

type SubscriptionItemProps = {
  subscription: Subscription;
  paymentType: "month" | "year";
  activeSubscription: Subscription;
  plannedDowngrade?: PaymentsSubscriptionStatusResponseDto;
  perks: { text: string; feature: number | string | ReactNode }[];
};

const SubscriptionItem = ({
  subscription,
  activeSubscription,
  paymentType,
  plannedDowngrade,
  perks,
}: SubscriptionItemProps) => {
  const { t } = useTranslation();
  const { yearlyDiscountInPercent, isActiveSubscription, formattedSubscriptionPrice, remainingDaysToDowngrade } =
    useSubscription({
      subscription,
      activeSubscription,
      paymentType,
      plannedDowngrade,
    });

  return (
    <div
      className={clsx(
        "h-full w-full rounded-2xl border p-7",
        isActiveSubscription && "border-brand-primary-subtle bg-brand-primary-subtle shadow-2xl",
        !isActiveSubscription && "border-secondary shadow-md",
      )}
    >
      <div className={"flex items-center gap-2"}>
        <div className={"text-xl font-semibold text-primary"}>{subscription.name}</div>
        {yearlyDiscountInPercent !== 0 && (
          <Badge label={t("settings.plans.freeMonths", { count: 2 })} color={"success"} />
        )}
      </div>
      <div className="mt-2 flex items-end gap-2">
        <div className="text-4xl font-bold text-brand-primary">{formattedSubscriptionPrice}</div>
        <div className="pb-1 text-sm text-quaternary">
          / {paymentType === "month" ? t("settings.plans.perMonth") : t("settings.plans.perYear")}
        </div>
      </div>
      <div className="mt-4 border-t border-t-secondary pt-4">
        <div className="flex flex-col pb-8">
          {perks.map((perk) => (
            <SubscriptionItemFeature feature={perk.feature} text={perk.text} />
          ))}
        </div>
        <SubscriptionItemButton
          subscription={subscription}
          paymentType={paymentType}
          activeSubscription={activeSubscription}
          remainingDaysToDowngrade={remainingDaysToDowngrade}
          plannedDowngrade={plannedDowngrade}
        />
      </div>
    </div>
  );
};

export default SubscriptionItem;
